import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

export type UsePortalPositionProps = {
  triggerRef: RefObject<HTMLElement>;
  open: boolean;
  offset?: number;
  alignment?: 'start' | 'end';
  matchTriggerWidth?: boolean;
  maxPanelHeight?: number;
  /**
   * Called when the trigger scrolls out of the viewport — either above (typically
   * behind a sticky header) or completely below the viewport. Dropdowns should
   * close themselves in response so they don't linger detached from their owner.
   */
  onTriggerOutOfView?: () => void;
};

export type UsePortalPositionResult = {
  panelStyle: CSSProperties;
};

const DEFAULT_OFFSET = 4;
const DEFAULT_MAX_PANEL_HEIGHT = 320;
const HEADER_SELECTOR = '.layout__header, .admin-head, [role="banner"]';

function getHeaderHeight(): number {
  if (typeof document === 'undefined') return 0;
  const el = document.querySelector(HEADER_SELECTOR);
  if (!el) return 0;
  return Math.round(el.getBoundingClientRect().height);
}

function isInsideDialog(node: HTMLElement | null): boolean {
  if (!node || typeof Element === 'undefined') return false;
  return node.closest('[role="dialog"]') != null;
}

function isInsideHeader(node: HTMLElement | null): boolean {
  if (!node || typeof Element === 'undefined') return false;
  return node.closest(HEADER_SELECTOR) != null;
}

export function usePortalPosition({
  triggerRef,
  open,
  offset = DEFAULT_OFFSET,
  alignment = 'start',
  matchTriggerWidth = false,
  maxPanelHeight = DEFAULT_MAX_PANEL_HEIGHT,
  onTriggerOutOfView,
}: UsePortalPositionProps): UsePortalPositionResult {
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  // Snapshot the callback in a ref so the effect doesn't tear down on every
  // render of a parent that recreates the function.
  const outOfViewRef = useRef(onTriggerOutOfView);
  outOfViewRef.current = onTriggerOutOfView;

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') {
      setPanelStyle({});
      return;
    }
    const node = triggerRef.current;
    if (!node) {
      setPanelStyle({});
      return;
    }

    const inDialog = isInsideDialog(node);
    const inHeader = isInsideHeader(node);
    // Triggers that live INSIDE the header (e.g. LanguageDropdown) need a
    // z-index high enough to escape the header's own stacking context;
    // otherwise the portaled panel renders behind it. Reuse the
    // in-modal token (9300) so the panel sits above the header (100)
    // and the rest of the page chrome.
    const zIndexVar =
      inDialog || inHeader ? 'var(--ddm-z-dropdown-in-modal)' : 'var(--ddm-z-dropdown)';

    let frame = 0;
    const compute = () => {
      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // When the trigger scrolls out of view — past the top edge of the
      // viewport, behind a sticky header (only when we are NOT inside a
      // modal — modals scroll independently of the page), or completely
      // below the viewport — ask the caller to close the dropdown rather
      // than leaving it orphaned. The dialog exemption avoids closing the
      // dropdown when the modal content (not the page) scrolls.
      //
      // We skip the check when the rect is degenerate (width/height both 0),
      // which is what jsdom returns for any element that hasn't been laid
      // out by a real layout engine — every Vitest assertion would otherwise
      // close the dropdown immediately on mount.
      const hasLayout = rect.width > 0 || rect.height > 0;
      // Header-overlap detection only makes sense when the trigger lives
      // OUTSIDE the header (body-anchored dropdown scrolling behind the
      // sticky bar). Triggers IN the header (LanguageDropdown) are always
      // visible while the header itself is visible — skip the check.
      // Modals scroll independently of the page → also skip.
      const headerHeight = inDialog || inHeader ? 0 : getHeaderHeight();
      const triggerHidden =
        hasLayout &&
        (rect.bottom <= headerHeight ||
          rect.top >= viewportH ||
          rect.right <= 0 ||
          rect.left >= window.innerWidth);
      if (triggerHidden) {
        outOfViewRef.current?.();
        return;
      }
      const spaceBelow = viewportH - rect.bottom;
      const spaceAbove = rect.top - headerHeight;
      const flipUp = spaceBelow < maxPanelHeight && spaceAbove > spaceBelow;
      const next: CSSProperties = {
        position: 'fixed',
        zIndex: zIndexVar as unknown as number,
      };
      if (flipUp) {
        next.bottom = Math.round(viewportH - rect.top + offset);
      } else {
        next.top = Math.round(rect.bottom + offset);
      }
      if (alignment === 'end') {
        next.right = Math.round(window.innerWidth - rect.right);
      } else {
        next.left = Math.round(rect.left);
      }
      if (matchTriggerWidth) {
        next.width = Math.round(rect.width);
      }
      next.maxHeight = maxPanelHeight;
      setPanelStyle(next);
    };

    const scheduleCompute = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        compute();
      });
    };

    compute();
    window.addEventListener('scroll', scheduleCompute, true);
    window.addEventListener('resize', scheduleCompute);
    return () => {
      window.removeEventListener('scroll', scheduleCompute, true);
      window.removeEventListener('resize', scheduleCompute);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [open, triggerRef, offset, alignment, matchTriggerWidth, maxPanelHeight]);

  return { panelStyle };
}
